import type { ClientEnvironment } from './target';

/**
 * The environment this bundle was built for.
 *
 * ## Why the screens have to know
 *
 * The Backoffice ships once per environment (ADR 0008): the production build
 * runs in the production account and holds a role into production and nothing
 * else. So the environment switcher was never really a switcher — three of its
 * four options were requests the running deployment cannot make, and picking
 * one produced an upstream error that read like an outage rather than like a
 * question that could not be asked from here.
 *
 * What a deployment can reach is {@link reachableEnvironments}, which lives with
 * the target it constrains. This file holds the one thing that cannot be a pure
 * function: reading what the build was stamped with.
 *
 * ## Why the fallback is `dev`
 *
 * `dev` rather than `prd` when unset, and that is the whole safety argument:
 * this value decides which backend the screens — which also *write* — are
 * pointed at, and a default that lands on production is a default that
 * eventually creates a client there by accident. Unset means a local
 * `npm run dev`, which has no deploy at all; the safe end of the ladder is the
 * one to guess.
 *
 * A build for production refuses to be produced without one at all —
 * `vite.config.ts` checks it. So a forgotten variable is a failed CI job naming
 * the variable, not a shipped bundle that offers `dev` on production and gets
 * 400s the operator can only read as being about the target.
 */

/**
 * The environments the Backoffice is deployed INTO.
 *
 * Deliberately narrower than `CLIENT_ENVIRONMENTS`: `eph` is a target a
 * deployment can NAME, never one it IS — review slots are backend stages, and
 * the Backoffice does not ship into one.
 *
 * The API's `clients-upstream.config.ts` holds the same list for the same
 * reason, and refuses `eph` in `BACKOFFICE_ENV`. Two halves of one fact, now
 * under one name: `VITE_BACKOFFICE_ENV` is this one, prefixed only because Vite
 * requires it of anything inlined into the bundle.
 */
export const DEPLOYABLE_ENVIRONMENTS = ['dev', 'stg', 'prd'] as const;

export type DeployEnvironment = (typeof DEPLOYABLE_ENVIRONMENTS)[number];

/**
 * The pure core, so the cases that matter are testable without a bundle.
 *
 * It falls back rather than throwing, unlike the API's. By the time this runs
 * in a browser the build-time check has already refused every production bundle
 * without a valid value, so the only reachable bad input is a local mistake —
 * and a white screen is a worse answer to that than `dev` is.
 */
export function resolveDeployEnvironment(raw: string | undefined): DeployEnvironment {
    const value = raw?.trim();
    return (DEPLOYABLE_ENVIRONMENTS as readonly string[]).includes(value ?? '')
        ? (value as DeployEnvironment)
        : 'dev';
}

export function deployEnvironment(): ClientEnvironment {
    return resolveDeployEnvironment(import.meta.env.VITE_BACKOFFICE_ENV);
}
